import { ThumbDownAlt, ThumbDownOffAlt, ThumbUpAlt, ThumbUpOffAlt } from "@mui/icons-material";
import { Box, IconButton, SxProps, Tooltip, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LLMResponse } from "../../../api/types/chatTypes";
import { FeedbackRequest, FeedbackSentiment } from "../../../api/types/feedbackTypes";
import CopyIconButton from "../../Common/CopyIconButton";

type FeedbackButtonsProps = {
    answer: LLMResponse;
    currentFeedback: FeedbackRequest | undefined;
    openFeedbackDialog: (feedback: FeedbackSentiment) => void;
    textToCopy: string;
};

/**
 * FeedbackButtons Component
 *
 * Renders interactive thumbs-up and thumbs-down icons for user sentiment feedback.
 * Highlights the selected sentiment if feedback has already been given.
 *
 * Props:
 * - `currentFeedback`: The current feedback object used to determine selected state.
 * - `handleFeedback`: Function to handle user feedback submission.
 * - `textToCopy`: The text that should be copied to clipboard
 *
 * @component
 * @param {FeedbackButtonsProps} props - The props for the component.
 * @returns {JSX.Element} A UI element with feedback sentiment buttons.
 *
 * @example
 * ```tsx
 * <FeedbackButtons
 *   currentFeedback={userFeedback}
 *   handleFeedback={(feedback) => console.log(feedback)}
 *   textToCopy={"Current answer!"}
 * />
 * ```
 */
function FeedbackButtons({ answer, currentFeedback, openFeedbackDialog, textToCopy }: FeedbackButtonsProps) {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "chat" });

    const isPositive = useMemo(() => currentFeedback?.feedbackSentiment === "positive", [currentFeedback]);
    const isNegative = useMemo(() => currentFeedback?.feedbackSentiment === "negative", [currentFeedback]);

    const getIconStyles = (): SxProps => ({
        width: 22,
        height: 22,
        color: "action.activate",
        transition: "opacity 0.3s ease, color 0.3s ease",
        "&:hover": {
            opacity: 1,
            cursor: "pointer"
        }
    });

    return (
        <Box sx={{ display: "flex", marginLeft: "auto", alignItems: "center", gap: "5px" }}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-end", gap: "2px" }}>
                <Typography sx={{ color: theme.palette.text.primary, fontSize: "1rem" }}>{answer.answerTime}</Typography>
                <CopyIconButton defaultText={t("answerAction.copyAnswer")} copyText={t("answerAction.answerCopied")} textToCopy={textToCopy} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "2px" }}>
                <Tooltip title={t("feedback.goodAnswer")} arrow>
                    <IconButton size="small" onClick={() => openFeedbackDialog("positive")}>
                        {isPositive ? (
                            <ThumbUpAlt
                                sx={{
                                    ...getIconStyles(),
                                    opacity: 1
                                }}
                            />
                        ) : (
                            <ThumbUpOffAlt
                                sx={{
                                    ...getIconStyles(),
                                    opacity: 0.7
                                }}
                            />
                        )}
                    </IconButton>
                </Tooltip>
                <Tooltip title={t("feedback.badAnswer")} arrow>
                    <IconButton size="small" onClick={() => openFeedbackDialog("negative")}>
                        {isNegative ? (
                            <ThumbDownAlt
                                sx={{
                                    ...getIconStyles(),
                                    opacity: 1
                                }}
                            />
                        ) : (
                            <ThumbDownOffAlt
                                sx={{
                                    ...getIconStyles(),
                                    opacity: 0.7
                                }}
                            />
                        )}
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

export default FeedbackButtons;
